import { useI18n } from "@baton/i18n";
import { cva } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cn } from "../../../cn.ts";
import { ariaKeyShortcuts } from "../../display/Kbd/keys.ts";
import { ShortcutHint } from "../../display/ShortcutHint/ShortcutHint.tsx";
import { Spinner } from "../../display/Spinner/Spinner.tsx";
import { useDisabledReason } from "../unavailable.tsx";

/**
 * § Primitives, Button, taken from Spectrum UI's Loading Button
 * (`@spectrumui/loading-button-dependencies`, Apache-2.0) and themed through the variables of
 * `packages/tokens` (2026-09-15, by the owner's decision). Baton's variant names stay, because
 * the screens call them, and each takes the Spectrum UI variant it stands for: primary is
 * `default`, secondary is `outline`, ghost is `ghost` and danger is `destructive`; danger-ghost
 * has no Spectrum UI variant and is its ghost with the destructive text. tinted (2026-10-03, by the
 * owner's word on the milestone move button: "I like the style of the milestone move button but
 * perhaps it should have a color") has none either: it is the outline variant drawn in the Team
 * accent, its soft fill, its text and an edge of the accent line softened into the fill, a step
 * between the quiet secondary and the filled primary. Its hover and press deepen the fill and the
 * label together, through the derived tokens of `packages/tokens`, which keep the label at 4.5:1 on
 * every Team's colour. The loading state is the Loading Button's: the spinner replaces the start icon
 * and the label stays.
 *
 * What differs from the source, and why:
 * - a solid fill's hover and press lay the overlay of § Semantic tokens over it, where
 *   Spectrum UI lightens the fill to 90 percent, because the lightened blue takes its white
 *   label below 4.5:1;
 * - the heights read the density's control heights rather than `h-9`, `h-10` and `h-11`, so
 *   compact density still steps each down 4 px (§ Density);
 * - a disabled button is drawn neutral grey, the disabled ink on the active grey fill for a filled or
 *   edged variant and the disabled ink alone for a ghost, where the source fades the variant to 50
 *   percent opacity, because a faded Team-accent fill still read as a button to press (2026-10-03, by
 *   the owner's approval of the product review: Save changes, Post and Add tasks looked clickable);
 * - the focus ring classes are not carried, because the one `:focus-visible` rule of
 *   `theme/focus.css` draws the same 2 px ring at a 2 px offset on every control (§ Focus);
 * - a secondary, tinted or ghost button that opens a menu keeps the pressed fill while the menu is
 *   open, which the Loading Button has no state for;
 * - `asChild` and the `link` variant are not carried, because nothing calls them; a link drawn
 *   as a button takes `buttonVariants`;
 * - `shortcut` names the key that runs the button inside the button, after its label, as quiet
 *   text in the platform's glyphs ("Create client ⌘↩", the `ShortcutHint` every key hint shares since
 *   2026-10-15), and as its `aria-keyshortcuts`, because a
 *   shortcut is part of the control it runs and a hint beside the buttons read as a control of its
 *   own (the owner's call of 2026-09-28). It is not drawn below `--bp-md`, where a phone has no
 *   keyboard to press it with; the host still binds the key. On the variants whose label ink stands
 *   at the 4.5:1 floor on their fill, the hint takes the label's full ink rather than 70 percent of
 *   it (`HINT_AT_LABEL_INK`, 2026-10-23);
 * - `disabledReason` (2026-10-10) says why a disabled button cannot be pressed. A natively disabled
 *   button takes no focus and fires no pointer event, so the `title` the screens had put on it reached
 *   no one; a button disabled with a reason is `aria-disabled` instead, stays in the focus order and
 *   under the pointer, runs nothing, keeps the disabled look, and opens its reason as a `Tooltip` on
 *   hover and on keyboard focus, the reason being also its accessible description; the whole rule is
 *   `useDisabledReason` of `unavailable.tsx`, which `PlainButton` shares. A button disabled with no
 *   reason stays natively disabled. The disabled look is drawn from `aria-disabled`, which every
 *   disabled or loading button carries, rather than from `:disabled`, so both read the same.
 */
export type ButtonVariant = "primary" | "secondary" | "tinted" | "ghost" | "danger" | "danger-ghost";
export type ButtonSize = "sm" | "md" | "lg";

/**
 * § Key hints (2026-10-23, found by the gallery run): the variants whose label ink stands at the
 * 4.5:1 floor on their fill, white on the Team's solid (4.52:1 on the static blue, and a Team palette
 * darkens its solid only as far as white needs), white on the red solid, the accent text on its tint
 * (4.55:1) and the red text on the sheet. The quiet 70 percent of the label's ink falls to about 3:1
 * there (3.04:1 on the blue solid), so the hint takes the label's full ink and is told from the label
 * by its regular weight against the label's medium. On the secondary and the ghost, whose label is
 * the primary ink, the 70 percent holds 5.1:1 or more on every fill they take.
 */
const HINT_AT_LABEL_INK: ReadonlySet<ButtonVariant> = new Set(["primary", "danger", "tinted", "danger-ghost"]);

export const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap select-none transition-colors disabled:pointer-events-none aria-disabled:not-aria-busy:text-text-disabled [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "accent-edge bg-primary text-primary-foreground hover:bg-[image:linear-gradient(var(--color-solid-hover-overlay),var(--color-solid-hover-overlay))] active:bg-[image:linear-gradient(var(--color-solid-active-overlay),var(--color-solid-active-overlay))]",
        secondary: "border border-input bg-background hover:bg-accent hover:text-accent-foreground active:bg-secondary aria-expanded:bg-secondary data-[state=open]:bg-secondary",
        tinted:
          "border border-accent-tint-border bg-accent-bg text-accent-text hover:bg-accent-bg-hover hover:text-accent-text-strong active:bg-accent-bg-active active:text-accent-text-strong aria-expanded:bg-accent-bg-active aria-expanded:text-accent-text-strong data-[state=open]:bg-accent-bg-active data-[state=open]:text-accent-text-strong",
        ghost: "hover:bg-accent hover:text-accent-foreground active:bg-secondary aria-expanded:bg-secondary data-[state=open]:bg-secondary",
        danger:
          "bg-destructive text-destructive-foreground hover:bg-[image:linear-gradient(var(--color-solid-hover-overlay),var(--color-solid-hover-overlay))] active:bg-[image:linear-gradient(var(--color-solid-active-overlay),var(--color-solid-active-overlay))]",
        "danger-ghost": "text-accent-red-text hover:bg-accent-red-bg active:bg-accent-red-bg",
      },
      size: {
        sm: "h-(--control-height-sm) px-3",
        md: "h-(--control-height-md) px-4",
        lg: "h-(--control-height-lg) px-8",
      },
      iconOnly: {
        true: "px-0",
        false: "",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
      pending: {
        true: "opacity-70",
        false: "",
      },
    },
    compoundVariants: [
      // A disabled button is neutral grey whatever its variant, so a commit that cannot run never looks like one that can (2026-10-03): a filled or edged one takes the grey fill with no edge and no overlay, a ghost one only the grey label. A loading button, disabled while it works, keeps its variant's look with the spinner. The `aria-disabled` rules sort after `hover:` and `active:`, so a button disabled with a reason, which stays under the pointer for its tooltip, takes no hover or press tint.
      { variant: ["primary", "secondary", "tinted", "danger"], class: "aria-disabled:not-aria-busy:border-transparent aria-disabled:not-aria-busy:bg-bg-active aria-disabled:not-aria-busy:bg-none aria-disabled:not-aria-busy:outline-none" },
      { variant: ["ghost", "danger-ghost"], class: "aria-disabled:not-aria-busy:bg-transparent" },
      { iconOnly: true, size: "sm", class: "w-(--control-height-sm)" },
      { iconOnly: true, size: "md", class: "w-(--control-height-md)" },
      { iconOnly: true, size: "lg", class: "w-(--control-height-lg)" },
    ],
    defaultVariants: {
      variant: "secondary",
      size: "md",
      iconOnly: false,
      fullWidth: false,
      pending: false,
    },
  },
);

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly iconStart?: ReactNode;
  readonly iconEnd?: ReactNode;
  readonly loading?: boolean;
  readonly pending?: boolean;
  readonly fullWidth?: boolean;
  /** The key that runs the button, as the shortcut map writes it ("Mod+Enter"); the host binds it. */
  readonly shortcut?: string;
  /** Why the button cannot be pressed while `disabled`: its tooltip on hover and keyboard focus, and its accessible description. `null` is no reason at the moment; the button keeps its tooltip, shut, and so its element and its focus. */
  readonly disabledReason?: string | null | undefined;
  readonly children: ReactNode;
  readonly ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = "secondary",
  size = "md",
  iconStart,
  iconEnd,
  loading = false,
  pending = false,
  fullWidth = false,
  shortcut,
  disabled,
  disabledReason,
  type = "button",
  className,
  children,
  ref,
  "aria-describedby": describedBy,
  ...rest
}: ButtonProps) {
  const { t } = useI18n();
  const reason = useDisabledReason({ disabled: disabled === true, loading, reason: disabledReason, describedBy, props: rest });
  return reason.withTip(
    <button
      ref={ref}
      type={type}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-pending={pending ? "true" : undefined}
      className={cn(buttonVariants({ variant, size, fullWidth, pending }), className)}
      aria-busy={loading || undefined}
      {...(shortcut === undefined ? {} : { "aria-keyshortcuts": ariaKeyShortcuts(shortcut) })}
      {...reason.props}
    >
      {loading ? (
        <Spinner size="sm" label={t("ui.button.loading")} className="text-current" />
      ) : (
        iconStart !== undefined && (
          <span className="inline-flex" aria-hidden="true">
            {iconStart}
          </span>
        )
      )}
      {children}
      {iconEnd !== undefined && (
        <span className="inline-flex" aria-hidden="true">
          {iconEnd}
        </span>
      )}
      {shortcut !== undefined && <ShortcutHint keys={shortcut} ink={HINT_AT_LABEL_INK.has(variant) ? "label" : "quiet"} className="max-md:hidden" />}
      {reason.note}
    </button>,
  );
}
